import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import QuickbooksConnectCard from './QuickbooksConnectCard';
import KeystoneIntegrationSettings from '../../components/Keystone/KeystoneIntegrationSettings';
import IdexxIntegrationSettings from '../../components/Idexx/IdexxIntegrationSettings';
import IdexxPanelManager from '../../components/Idexx/IdexxPanelManager';
import { skipWhenOffline, OfflineNotice, useIsOffline } from '../../offline';

interface StripeRequirementError {
  code: string;
  reason: string;
  requirement: string;
}

interface StripeConnectStatus {
  connected: boolean;
  accountId: string | null;
  status: 'pending' | 'in_progress' | 'complete' | 'restricted' | 'verifying' | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted?: boolean;
  disabledReason?: string | null;
  currentlyDue?: string[];
  pastDue?: string[];
  pendingVerification?: string[];
  errors?: StripeRequirementError[];
  currentDeadline?: string | null;
  canManage: boolean;
  cached?: boolean;
}

interface Integration {
  name: string;
  description: string;
  isConnected: boolean;
}

const requirementLabels: Record<string, string> = {
  'external_account': 'Bank account for payouts',
  'individual.verification.document': 'Identity document (e.g. driver\'s license, passport)',
  'individual.verification.additional_document': 'Additional identity document',
  'individual.dob.day': 'Date of birth',
  'individual.dob.month': 'Date of birth',
  'individual.dob.year': 'Date of birth',
  'individual.first_name': 'Legal first name',
  'individual.last_name': 'Legal last name',
  'individual.address.line1': 'Home address',
  'individual.address.city': 'City',
  'individual.address.state': 'State / Province',
  'individual.address.postal_code': 'Postal code',
  'individual.ssn_last_4': 'Last 4 digits of SSN',
  'individual.id_number': 'Government-issued ID number',
  'individual.phone': 'Phone number',
  'individual.email': 'Email address',
  'business_profile.url': 'Business website URL',
  'business_profile.mcc': 'Business category',
  'business_profile.product_description': 'Description of goods/services',
  'company.name': 'Legal business name',
  'company.tax_id': 'Tax ID (EIN)',
  'company.address.line1': 'Business address',
  'company.address.city': 'Business city',
  'company.address.state': 'Business state / province',
  'company.address.postal_code': 'Business postal code',
  'company.phone': 'Business phone number',
  'company.verification.document': 'Business verification document',
  'tos_acceptance.date': 'Terms of Service acceptance',
  'tos_acceptance.ip': 'Terms of Service acceptance',
};

function formatRequirement(field: string): string {
  if (requirementLabels[field]) return requirementLabels[field];
  // Fall back to a cleaned-up version of the field name
  return field
    .replace(/^(individual|company|business_profile)\./, '')
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDeadline(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  const formatted = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  if (diffDays < 0) return `${formatted} (overdue)`;
  if (diffDays === 0) return `${formatted} (today)`;
  if (diffDays === 1) return `${formatted} (tomorrow)`;
  if (diffDays <= 7) return `${formatted} (${diffDays} days left)`;
  return formatted;
}

const IntegrationsSettings: React.FC = () => {
  const isOffline = useIsOffline();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // Stripe Connect state
  const [stripeConnectStatus, setStripeConnectStatus] = useState<StripeConnectStatus | null>(null);
  // Distinct from the error/not-connected states: offline we simply don't know,
  // and must not fabricate a "Not Connected" chip for a clinic with a live account.
  const [stripeStatusUnavailable, setStripeStatusUnavailable] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Check URL params for callback results
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeConnectSuccess = params.get('stripe_connect_success');
    const stripeConnectError = params.get('stripe_connect_error');
    const stripeReturn = params.get('stripe_return');
    const stripeRefresh = params.get('stripe_refresh');

    if (stripeConnectSuccess === 'true') {
      setSuccessMessage('Successfully connected your Stripe account!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    } else if (stripeConnectError) {
      setErrorMessage(decodeURIComponent(stripeConnectError));
      setTimeout(() => setErrorMessage(''), 8000);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    } else if (stripeReturn === 'true') {
      // User returned from Stripe onboarding
      setSuccessMessage('Stripe account setup updated. Checking status...');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    } else if (stripeRefresh === 'true') {
      // User's onboarding link expired, they need a new one
      setErrorMessage('Your setup session expired. Please click "Complete Setup" to continue.');
      setTimeout(() => setErrorMessage(''), 8000);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    }
  }, []);

  // Fetch Stripe Connect status
  const fetchStripeConnectStatus = useCallback(async () => {
    // Offline: skip the fetch entirely — a failed call would fabricate a
    // "Not Connected" state for a clinic with an active Stripe account.
    if (!skipWhenOffline()) {
      setStripeConnectStatus(null);
      setStripeStatusUnavailable(true);
      setStripeLoading(false);
      return;
    }
    try {
      setStripeLoading(true);
      setStripeStatusUnavailable(false);
      const response = await api.get('/stripe-connect/status');
      setStripeConnectStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch Stripe Connect status:', err);
      setStripeConnectStatus({
        connected: false,
        accountId: null,
        status: null,
        chargesEnabled: false,
        payoutsEnabled: false,
        canManage: false,
      });
    } finally {
      setStripeLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStripeConnectStatus();
  }, [fetchStripeConnectStatus]);

  // Reconnect: replace the offline "status unknown" placeholder with real data.
  useEffect(() => {
    if (!isOffline && stripeStatusUnavailable) fetchStripeConnectStatus();
  }, [isOffline, stripeStatusUnavailable, fetchStripeConnectStatus]);

  // Create new Stripe Standard account and redirect to onboarding
  const handleSetupPayments = async () => {
    try {
      setActionLoading(true);
      setErrorMessage('');
      
      // Create the connected account
      await api.post('/stripe-connect/create-account', {});
      
      // Get the onboarding link and redirect
      const response = await api.post('/stripe-connect/onboarding-link');
      const { url } = response.data;
      
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (err: any) {
      console.error('Failed to set up payments:', err);
      setErrorMessage(
        err.response?.data?.message || 'Failed to set up payments. Please try again.'
      );
      setActionLoading(false);
    }
  };

  // Resume onboarding for incomplete accounts
  const handleResumeOnboarding = async () => {
    try {
      setActionLoading(true);
      setErrorMessage('');
      
      const response = await api.post('/stripe-connect/onboarding-link');
      const { url } = response.data;
      
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (err: any) {
      console.error('Failed to get onboarding link:', err);
      setErrorMessage(
        err.response?.data?.message || 'Failed to continue setup. Please try again.'
      );
      setActionLoading(false);
    }
  };

  // Connect existing Stripe account via OAuth
  const handleConnectExisting = async () => {
    try {
      setActionLoading(true);
      setErrorMessage('');
      
      const response = await api.post('/stripe-connect/oauth-link', {});
      const { oauthUrl } = response.data;
      
      if (oauthUrl) {
        // Redirect to Stripe OAuth
        window.location.href = oauthUrl;
      } else {
        throw new Error('No OAuth URL returned');
      }
    } catch (err: any) {
      console.error('Failed to generate OAuth link:', err);
      setErrorMessage(
        err.response?.data?.message || 'Failed to connect Stripe account. Please try again.'
      );
      setActionLoading(false);
    }
  };

  // Open Stripe dashboard
  const handleOpenDashboard = async () => {
    try {
      const response = await api.get('/stripe-connect/dashboard-link');
      const { url } = response.data;
      
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err: any) {
      console.error('Failed to get dashboard link:', err);
      setErrorMessage(
        err.response?.data?.message || 'Unable to open Stripe dashboard. Please complete onboarding first.'
      );
    }
  };

  // Disconnect Stripe account
  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      setErrorMessage('');
      
      await api.post('/stripe-connect/disconnect');
      
      setShowDisconnectConfirm(false);
      setSuccessMessage('Stripe account disconnected successfully.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
      
      // Refresh status
      await fetchStripeConnectStatus();
    } catch (err: any) {
      console.error('Failed to disconnect Stripe account:', err);
      setErrorMessage(
        err.response?.data?.message || 'Failed to disconnect Stripe account. Please try again.'
      );
    } finally {
      setDisconnecting(false);
    }
  };

  // Render Stripe Connect status badge
  const renderStatusBadge = () => {
    if (stripeStatusUnavailable) {
      return <Chip label="Status unavailable offline" color="default" size="small" variant="outlined" />;
    }

    if (!stripeConnectStatus?.connected) {
      return <Chip label="Not Connected" color="default" size="small" />;
    }

    if (stripeConnectStatus.status === 'complete' && stripeConnectStatus.chargesEnabled) {
      return <Chip label="Active" color="success" size="small" icon={<CheckCircleIcon />} />;
    }

    if (stripeConnectStatus.status === 'restricted') {
      return <Chip label="Restricted" color="error" size="small" icon={<WarningIcon />} />;
    }

    if (stripeConnectStatus.status === 'verifying') {
      return <Chip label="Verification in Progress" color="info" size="small" />;
    }

    if (stripeConnectStatus.status === 'in_progress') {
      return <Chip label="Action Required" color="warning" size="small" icon={<WarningIcon />} />;
    }

    if (stripeConnectStatus.status === 'pending') {
      return <Chip label="Setup Incomplete" color="warning" size="small" icon={<WarningIcon />} />;
    }

    return <Chip label="Connected" color="info" size="small" />;
  };

  // Render Stripe Connect card content
  const renderStripeConnectContent = () => {
    if (stripeLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <CircularProgress size={24} />
          <Typography color="text.secondary">Loading payment status...</Typography>
        </Box>
      );
    }

    // Offline / status-unknown — the OfflineNotice in the card body explains it.
    if (stripeStatusUnavailable) {
      return null;
    }

    // Not connected state
    if (!stripeConnectStatus?.connected) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your Stripe account to accept credit card, ACH, and other payments directly in StableTrack.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              onClick={handleConnectExisting}
              disabled={actionLoading || !stripeConnectStatus?.canManage || isOffline}
            >
              {actionLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              Connect Stripe Account
            </Button>
            <Button
              variant="outlined"
              onClick={handleSetupPayments}
              disabled={actionLoading || !stripeConnectStatus?.canManage || isOffline}
            >
              Create New Stripe Account
            </Button>
          </Stack>
          {!stripeConnectStatus?.canManage && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Only clinic owners and admins can manage payment settings.
            </Typography>
          )}
        </Box>
      );
    }

    // Connected state
    return (
      <Box sx={{ mt: 2 }}>
        {/* Status indicators */}
        <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Charges
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {stripeConnectStatus.chargesEnabled ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : stripeConnectStatus.status === 'verifying' ? (
                <CircularProgress size={16} />
              ) : (
                <WarningIcon color="warning" fontSize="small" />
              )}
              <Typography variant="body2">
                {stripeConnectStatus.chargesEnabled
                  ? 'Enabled'
                  : stripeConnectStatus.status === 'verifying'
                    ? 'Verifying'
                    : 'Pending'}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Payouts
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {stripeConnectStatus.payoutsEnabled ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : stripeConnectStatus.status === 'verifying' ? (
                <CircularProgress size={16} />
              ) : (
                <WarningIcon color="warning" fontSize="small" />
              )}
              <Typography variant="body2">
                {stripeConnectStatus.payoutsEnabled
                  ? 'Enabled'
                  : stripeConnectStatus.status === 'verifying'
                    ? 'Verifying'
                    : 'Pending'}
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* Action needed: onboarding not finished */}
        {stripeConnectStatus.status === 'pending' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your account setup is incomplete. Please complete the setup to start accepting payments.
          </Alert>
        )}

        {/* Action needed: Stripe requires more info from the user */}
        {stripeConnectStatus.status === 'in_progress' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {stripeConnectStatus.pastDue && stripeConnectStatus.pastDue.length > 0
                ? 'Stripe needs additional information to keep your account active.'
                : 'Your account setup is incomplete. Please provide the required information.'}
            </Typography>

            {/* Show specific errors with human-readable reasons */}
            {stripeConnectStatus.errors && stripeConnectStatus.errors.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                {stripeConnectStatus.errors.map((err, i) => (
                  <Typography component="li" variant="body2" key={i}>
                    {err.reason}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Fall back to listing required fields if no errors array */}
            {(!stripeConnectStatus.errors || stripeConnectStatus.errors.length === 0) &&
              stripeConnectStatus.currentlyDue && stripeConnectStatus.currentlyDue.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Required information:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {Array.from(new Set(stripeConnectStatus.currentlyDue.map(formatRequirement))).map((label, i) => (
                    <Typography component="li" variant="body2" key={i}>
                      {label}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {stripeConnectStatus.currentDeadline && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                Deadline: {formatDeadline(stripeConnectStatus.currentDeadline)}
              </Typography>
            )}
          </Alert>
        )}

        {/* Passive: Stripe is verifying — no user action needed */}
        {stripeConnectStatus.status === 'verifying' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Stripe is verifying your account details. This usually takes a few minutes but can take up to 24 hours. Charges and payouts will be enabled automatically once verification is complete.
            </Typography>
            {stripeConnectStatus.pendingVerification && stripeConnectStatus.pendingVerification.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Being verified: {Array.from(new Set(stripeConnectStatus.pendingVerification.map(formatRequirement))).join(', ')}
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {/* Genuine restriction */}
        {stripeConnectStatus.status === 'restricted' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Your Stripe account has been restricted.
            </Typography>
            {stripeConnectStatus.disabledReason && (
              <Typography variant="body2">
                Reason: {stripeConnectStatus.disabledReason.replace(/_/g, ' ').replace(/^rejected\./, 'Rejected — ')}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Please visit your Stripe Dashboard or contact Stripe support to resolve this issue.
            </Typography>
          </Alert>
        )}

        {/* Action buttons */}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {stripeConnectStatus.status === 'pending' && (
            <Button
              variant="contained"
              onClick={handleResumeOnboarding}
              disabled={!stripeConnectStatus.canManage || actionLoading || isOffline}
            >
              {actionLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              Complete Setup
            </Button>
          )}

          {stripeConnectStatus.status === 'in_progress' && (
            <Button
              variant="contained"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenDashboard}
              disabled={isOffline}
            >
              Resolve in Stripe Dashboard
            </Button>
          )}
          
          {stripeConnectStatus.status !== 'in_progress' && (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenDashboard}
              disabled={isOffline}
            >
              Stripe Dashboard
            </Button>
          )}

          {stripeConnectStatus.canManage && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={() => setShowDisconnectConfirm(true)}
              disabled={isOffline}
            >
              Disconnect
            </Button>
          )}
        </Stack>
      </Box>
    );
  };

  const integrations: Integration[] = [
    {
      name: "SMS Service",
      description: "Send appointment reminders and notifications via SMS. Coming soon.",
      isConnected: false,
    }
  ];

  const showBanner = useCallback((message: string) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 5000);
  }, []);

  const showError = useCallback((message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 8000);
  }, []);

  return (
    <Box>
      {showSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage || 'Operation completed successfully'}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}
      
      <Stack spacing={2}>
        {/* Stripe Connect Card - Featured prominently */}
        <Card variant="outlined" sx={{ borderColor: 'primary.main', borderWidth: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ mr: 2 }}>
                    Payment Processing
                  </Typography>
                  {renderStatusBadge()}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Accept credit card, ACH, and other payments from your clients directly in StableTrack.
                </Typography>
                <OfflineNotice sx={{ mt: 2, mb: 0 }}>
                  Stripe status is unavailable offline — reconnect to manage payment processing.
                </OfflineNotice>
                {renderStripeConnectContent()}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <QuickbooksConnectCard onSuccess={showBanner} onError={showError} />

        <Divider sx={{ my: 2 }} />

        {/* Keystone Integration. Offline, render a placeholder card instead of
            mounting the component — its status fetch would fail and show a raw
            "Failed to load Keystone configuration" error plus a false
            "Not Configured" chip with a live Configure button. */}
        {isOffline ? (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6">Asteris Keystone</Typography>
                <Chip label="Status unavailable offline" size="small" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Medical imaging integration for X-Ray, Ultrasound, MRI, CT
              </Typography>
              <OfflineNotice sx={{ mb: 0 }}>
                Keystone status is unavailable offline — reconnect to manage the imaging integration.
              </OfflineNotice>
            </CardContent>
          </Card>
        ) : (
          <KeystoneIntegrationSettings />
        )}

        {/* IDEXX Integration — labreq + result polling */}
        <IdexxIntegrationSettings />

        {/* IDEXX lab panels (reusable test bundles) */}
        <IdexxPanelManager />

        {/* Other integrations */}
        {integrations.map((integration, index) => (
          <Card key={index} variant="outlined" sx={{ opacity: 0.7 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ mr: 2 }}>
                      {integration.name}
                    </Typography>
                    <Chip label="Coming Soon" size="small" variant="outlined" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {integration.description}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={showDisconnectConfirm} onClose={() => setShowDisconnectConfirm(false)}>
        <DialogTitle>Disconnect Stripe Account?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect your Stripe account? You will no longer be able to accept payments until you reconnect.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisconnectConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisconnect}
            disabled={disconnecting}
          >
            {disconnecting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegrationsSettings;
